import type { OnInit } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { FieldTree } from '@angular/forms/signals';

import { ChangeDetectionStrategy, Component, ElementRef, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';

import type { FormButtonSettings } from '@components/sp-form-button-settings';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type';
import type { ExtendedPositionLegacy } from '@sites/entity-core';
import type { PaymentFormWidgetForm } from '@sites/entity-signal-form';

import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
    EButtonStyleType,
    ESize,
} from '@common/enums';
import { EPaymentView } from '@common/enums/payment-form-view.enum';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';

import type { FormCssVars } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import {
    getFormAlignCssVars,
    getFormButtonCssClasses,
    getFormButtonCssVars,
    getFormRootCssClasses,
} from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import { getPaymentFormRootCssVars } from '@sites/entity-core/src/widget/payment-form/styles/payment-form.css-vars';
import { PaymentFormStaticViewComponent } from './payment-form-static-view/payment-form-static-view.component';
import { FORM_ROOT_THEME_TOKENS, colorCss } from '@web/widgets/common/css-vars/css-vars.utils';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

@Component({
    selector: 'web-payment-form-view',
    templateUrl: './payment-form-view.component.html',
    styleUrls: ['./payment-form-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PaymentFormStaticViewComponent],
})
export class PaymentFormViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<PaymentFormWidgetData>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public form!: PaymentFormWidgetForm;

    protected readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    protected readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    /**
     * Live entity straight from the signal form. Until `ngOnInit` runs (and the
     * form is materialised), fall back to the `@Input` snapshot.
     */
    public readonly entity = computed<WebLegacyWidgetEntity<PaymentFormWidgetData>>(
        () => (this.form?._raw().value() as unknown as WebLegacyWidgetEntity<PaymentFormWidgetData>) ?? this.widget()
    );

    public readonly data = computed<PaymentFormWidgetData>(() => this.entity().data);

    /** Root layout + input theming + input-size + alignment variables (cascade to descendants). */
    public readonly rootCssVars = computed<FormCssVars>(() => ({
        ...getPaymentFormRootCssVars(this.entity(), FORM_ROOT_THEME_TOKENS),
        ...getFormAlignCssVars(this.data().inputSettings),
    }));

    public readonly rootCssClasses = computed<string>(() => getFormRootCssClasses(this.data().inputSettings));

    /** Submit (modal-trigger) button variables — set on the wrapper, inherited by the anchor. */
    public readonly buttonCssVars = computed<FormCssVars>(() =>
        getFormButtonCssVars(
            this.data().formButtonSettings,
            this.data().inputSettings.formStyleType,
            colorCss,
            this.isZero()
        )
    );

    public readonly formButtonSettings = computed<FormButtonSettings>(() => this.data().formButtonSettings);

    public readonly isStaticForm = computed<boolean>(() => this.data().formView === EPaymentView.static);

    public readonly isModalForm = computed<boolean>(() => this.data().formView === EPaymentView.modal);

    public readonly getWidgetId = computed<string>(() => this.form?.displayId() ?? `widget-${this.widget().id}`);

    /** True when the widget lives inside a zero block. */
    public readonly isZero = computed<boolean>(() => this.entity().type === 'zero');

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<PaymentFormWidgetForm>(this.widget().id, this.destroyRef);

        if (this.isZero()) {
            this.setupZeroHeightSync();
        }
    }

    /**
     * Zero-block payment forms auto-grow with their content: the rendered root's
     * height is mirrored back into the extended-position grid so the zero host's
     * selection frame and boundary math track the real size (manual height
     * resize is disabled for PAYMENT_FORM in the zero host) — the form view's
     * `setupZeroHeightSync` pattern.
     */
    private setupZeroHeightSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => this.syncZeroHeightFromDom(),
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    private syncZeroHeightFromDom(): void {
        const root = this.elementRef.nativeElement.firstElementChild as HTMLElement | null;
        if (!root) return;

        const height = parseFloat(getComputedStyle(root).height);
        if (!Number.isFinite(height) || height <= 0) return;

        // The form's position accessor is union-typed (simple grid on default
        // placement) — this code path only runs for zero forms.
        const positionField = this.form.position as unknown as FieldTree<ExtendedPositionLegacy>;
        const position = positionField().value();

        if (this.isDeviceMobileSig()) {
            if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
            return;
        }

        if (this.isDeviceTabletSig()) {
            if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
            return;
        }

        const writeAll = () => {
            if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
            if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
            if (position.height !== height) positionField.height().value.set(height);
        };

        // Batch the three device writes into one store dispatch — unless an outer
        // transaction (settings panel editing) is already open: the plugin
        // doesn't support nesting, a nested `transact` would commit the outer
        // snapshot prematurely.
        if (this.form.inTransaction()) {
            writeAll();
        } else {
            this.form.transact(writeAll);
        }
    }

    public getButtonEffectClass(button: FormButtonSettings): string {
        let nameEffect: string = '';

        if (button.additionalEffects?.isActive) {
            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (button.additionalEffects?.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (button.additionalEffects?.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public getButtonStyles(formButtonSettings: FormButtonSettings): string {
        const base = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[formButtonSettings.size ? formButtonSettings.size : ESize.M]} form-button`;
        const themed = getFormButtonCssClasses(formButtonSettings);
        const type =
            formButtonSettings.styleType === EButtonStyleType.custom
                ? ''
                : BUTTON_TYPE_CLASS_MAP[formButtonSettings.styleType];

        return `${base} ${type} ${themed} ${this.getButtonEffectClass(formButtonSettings)}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }
}
